const MB_IN_BYTES = 1024 * 1024;
const WEBP_MIME_TYPE = "image/webp";

const ACCEPTED_IMAGE_TYPES = new Set([
  WEBP_MIME_TYPE,
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/avif",
  "image/bmp",
  "image/tiff",
  "image/heic",
  "image/heif",
]);

const ACCEPTED_IMAGE_EXT = /\.(webp|jpe?g|png|gif|avif|bmp|tiff?|heic|heif)$/i;

/** File picker: any common image; backend converts to WebP with Sharp. */
export const IMAGE_PICKER_ACCEPT =
  "image/jpeg,image/png,image/webp,image/gif,image/avif,image/bmp,.jpg,.jpeg,.png,.webp,.gif,.avif,.bmp";

export const isAcceptedImageFile = (file) => {
  if (!file) return false;
  if (file.type && ACCEPTED_IMAGE_TYPES.has(file.type)) return true;
  if (file.type && String(file.type).startsWith("image/")) return true;
  return ACCEPTED_IMAGE_EXT.test(file.name || "");
};

/**
 * Validate an image before upload. Format can be anything accepted;
 * the API converts to WebP on save.
 */
export const validateImageFile = (file, maxSizeMb = 5) => {
  if (!file) {
    return { isValid: false, message: "No file selected." };
  }

  if (!isAcceptedImageFile(file)) {
    return {
      isValid: false,
      message: "Please choose a valid image (JPG, PNG, WEBP, GIF, …).",
    };
  }

  if (file.size > maxSizeMb * MB_IN_BYTES) {
    return {
      isValid: false,
      message: `Image size must be ${maxSizeMb}MB or less.`,
    };
  }

  return { isValid: true, message: "" };
};

/** @deprecated Use validateImageFile — kept for existing imports. */
export const validateWebpImage = (file, maxSizeMb = 5) =>
  validateImageFile(file, maxSizeMb);

const loadImageFromFile = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image file."));
    };
    img.src = url;
  });

const canvasToWebpBlob = (canvas, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error("Image conversion failed.")),
      WEBP_MIME_TYPE,
      quality
    );
  });

const renderToCanvas = (img, maxDim) => {
  let { width, height } = img;
  if (maxDim && Math.max(width, height) > maxDim) {
    const scale = maxDim / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);
  return canvas;
};

/**
 * Optional client-side WebP prep (backend already converts with Sharp).
 */
export const prepareWebpFile = async (file, maxSizeMb = 5) => {
  if (!isAcceptedImageFile(file)) {
    throw new Error("Please choose a JPG, PNG, WEBP, or GIF image.");
  }

  const maxBytes = maxSizeMb * MB_IN_BYTES;
  if (file.size > maxBytes) {
    throw new Error(`Image is too large. Please use a file under ${maxSizeMb} MB.`);
  }

  if (file.type === WEBP_MIME_TYPE) {
    return file;
  }

  const img = await loadImageFromFile(file);
  const baseName = (file.name || "image").replace(/\.[^.]+$/i, "") || "image";
  let maxDim = Math.max(img.width, img.height);
  let quality = 0.92;

  for (let attempt = 0; attempt < 14; attempt += 1) {
    const canvas = renderToCanvas(img, maxDim);
    const blob = await canvasToWebpBlob(canvas, quality);
    if (blob.size <= maxBytes) {
      return new File([blob], `${baseName}.webp`, {
        type: WEBP_MIME_TYPE,
        lastModified: Date.now(),
      });
    }
    if (quality > 0.55) {
      quality -= 0.08;
    } else if (maxDim > 720) {
      maxDim = Math.round(maxDim * 0.85);
      quality = 0.85;
    } else {
      break;
    }
  }

  throw new Error(`Image is too large. Please use a file under ${maxSizeMb} MB.`);
};
